import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Clock, RefreshCw, Loader2, AlertTriangle, Zap, Layers } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  fmt, biasStyle, TradeSection, TradeCard, TrackRecordWidget, ZoneReasonCard, ErrorCard,
  CASCADE_STEPS, CASCADE_PROGRESS, CASCADE_STAGE_LABEL,
} from "@/components/AuraTradeShared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_MS = 5 * 60 * 1000; // 5 min

export default function WeeklySetups() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [lastReview, setLastReview] = useState(null);
  const [trackRecord, setTrackRecord] = useState(null);

  const fetchAll = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const [{ data }, reviewResp, trackResp] = await Promise.all([
        axios.get(`${API}/aura/intraday/all`),
        axios.get(`${API}/journal/last-trade-of-the-week/review`).catch(() => ({ data: { entry: null } })),
        axios.get(`${API}/aura/intraday/track-record?kind=week&limit=8`).catch(() => ({ data: null })),
      ]);
      setData(data);
      setLastReview(reviewResp.data?.entry || null);
      setTrackRecord(trackResp.data || null);
      setLastUpdated(new Date());
    } catch (err) {
      if (!silent) toast.error("Failed to load weekly zones — retrying shortly");
      setTimeout(() => fetchAll(true), 8000);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const t = setInterval(() => fetchAll(true), REFRESH_MS);
    return () => clearInterval(t);
  }, [fetchAll]);

  if (loading) {
    return (
      <div className="p-6 lg:p-8 space-y-6" data-testid="weekly-setups-loading">
        <Skeleton className="h-8 w-72" />
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-72" />)}
        </div>
      </div>
    );
  }

  const weeklyMeta = data?.weekly_trade;
  const hasInstruments = (data?.instruments || []).length > 0;
  const weekLabel = (data?.instruments || []).find((i) => i.week_label)?.week_label;

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-slide-up" data-testid="weekly-setups-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight uppercase" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
            Weekly Setups
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Swing zones for the week ahead · Weekly zone → Daily confirm → 4H confirm → 1H entry
          </p>
          {weekLabel && (
            <p className="text-xs text-amber-400/80 mt-1 font-medium" data-testid="week-window-label">
              Week window: {weekLabel}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Updated {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <Button variant="outline" onClick={() => fetchAll(false)} disabled={refreshing} data-testid="refresh-weekly-setups-btn">
            {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Rescan Now
          </Button>
        </div>
      </div>

      {/* Weekly zones for ALL assets - first thing to scan every week */}
      {(data?.weekly_cascades || []).length > 0 && (
        <WeeklyZonesOverview cascades={data.weekly_cascades} />
      )}

      {/* Trade of the Week - best swing pair, highest win probability */}
      {weeklyMeta && (
        <TradeSection meta={weeklyMeta} label="Trade of the Week" horizon="week" testidBase="trade-of-the-week"
          subtitle="Best swing pair right now - highest win probability across all 8 assets, picked for the strongest confluence AND the nearest realistic entry." />
      )}

      {/* Catch-Up Entries - Mon-Thu after 5pm UK close, for any asset whose Weekly
          Cascade hasn't confirmed yet: a next-day-actionable, week-hold idea so a
          slow cascade doesn't mean missing the week entirely. */}
      {(data?.catchup_entries || []).length > 0 && (
        <div className="space-y-3" data-testid="catchup-entries-section">
          <div>
            <h2 className="text-lg font-bold uppercase tracking-tight" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
              Catch-Up Entries
            </h2>
            <p className="text-xs text-muted-foreground">
              Today's Weekly Cascade didn't confirm for these assets yet - here's the next-best entry to take
              tomorrow and hold for the rest of the week, still built to a 1:5 (1:3 partial) reward:risk.
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {data.catchup_entries.map((entry) => (
              <div key={entry.symbol} data-testid={`catchup-entry-${entry.symbol}`}>
                <TradeCard trade={entry} label="Catch-Up Entry — Week Hold" testid={`catchup-entry-card-${entry.symbol}`} />
                <p className="text-xs text-amber-400/80 mt-1.5 px-1">{entry.catchup_reason}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Last Trade of the Week - Journal Review */}
      {lastReview && lastReview.review && (
        <Card data-testid="last-week-review-card" className="border-purple-500/30">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-purple-400 font-semibold">Last Week Review</div>
                <CardTitle className="text-base mt-1">
                  {lastReview.instrument_label || lastReview.symbol} · {lastReview.direction}
                </CardTitle>
              </div>
              <Badge className={`text-lg px-3 py-1 font-bold ${
                { A: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
                  B: "bg-teal-500/20 text-teal-300 border-teal-500/40",
                  C: "bg-amber-500/20 text-amber-300 border-amber-500/40",
                  D: "bg-orange-500/20 text-orange-300 border-orange-500/40",
                  F: "bg-red-500/20 text-red-300 border-red-500/40" }[lastReview.review.grade] || "bg-slate-500/20"
              }`}>
                Grade {lastReview.review.grade}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            <p>{lastReview.review.summary}</p>
            {lastReview.review.key_learning && (
              <div className="text-xs bg-amber-500/10 border border-amber-500/20 rounded p-2 mt-2">
                <span className="text-amber-400 font-medium">Key Learning:</span> {lastReview.review.key_learning}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Empty / error state */}
      {!hasInstruments && (
        <Card data-testid="weekly-setups-empty" className="border-slate-700/50">
          <CardContent className="py-12 flex flex-col items-center gap-3 text-center">
            <AlertTriangle className="w-8 h-8 text-amber-500/70" />
            <div className="text-sm text-muted-foreground">
              Couldn't load market data. This can happen if yfinance is rate-limited.
            </div>
            <Button variant="outline" size="sm" onClick={() => fetchAll(false)} data-testid="weekly-setups-retry-btn">
              <RefreshCw className="w-3 h-3 mr-2" />
              Try again
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Other assets, inline with cascade progress + buy/sell zones */}
      <div>
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">All Assets - Weekly Cascade &amp; Zones</div>
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {(data?.weekly_cascades || []).map((c) => (
            <WeeklyInstrumentCard key={c.symbol} cascade={c} />
          ))}
        </div>
      </div>

      {/* Weekly Track Record widget */}
      {trackRecord && trackRecord.stats?.sample_size > 0 && (
        <TrackRecordWidget data={trackRecord} />
      )}

      <p className="text-xs text-muted-foreground text-center pt-4">
        Zones auto-refresh every 5 minutes while this page is open. For informational purposes only — not financial advice.
      </p>
    </div>
  );
}

function WeeklyZonesOverview({ cascades }) {
  const rows = [...cascades].sort((a, b) => {
    const sa = a.weekly_zone?.conviction?.score_pct ?? -1;
    const sb = b.weekly_zone?.conviction?.score_pct ?? -1;
    return sb - sa;
  });
  return (
    <Card className="border-slate-700/50" data-testid="weekly-zones-overview">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Layers className="w-4 h-4 text-amber-400" /> Weekly Zones — All Assets
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Every asset's buy zone AND sell zone at a glance, best odds first - scan this before drilling into any single trade.
        </p>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {rows.map((c) => {
          const isRanging = c.cascade_stage === "ranging_no_trend";
          return (
            <div key={c.symbol} className="flex items-center gap-3 text-xs py-1.5 border-b border-slate-800/60 last:border-0"
              data-testid={`weekly-zone-row-${c.symbol}`}>
              <span className="w-32 shrink-0 font-medium truncate">{c.label}</span>
              {isRanging ? (
                <Badge className="bg-slate-600/20 text-slate-400 border-slate-600/40">RANGING - no trade</Badge>
              ) : (
                <>
                  <ZoneOverviewCell zone={c.buy_zone} kind="buy" />
                  <ZoneOverviewCell zone={c.sell_zone} kind="sell" />
                  <span className="text-muted-foreground w-44 text-right hidden md:inline truncate ml-auto">
                    {CASCADE_STAGE_LABEL[c.cascade_stage] || c.cascade_stage}
                  </span>
                </>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function ZoneOverviewCell({ zone, kind }) {
  const isBuy = kind === "buy";
  if (!zone) {
    return <span className="text-muted-foreground/50 w-40">No {isBuy ? "buy" : "sell"} zone</span>;
  }
  return (
    <span className="flex items-center gap-1.5 w-40">
      <Badge variant="outline" className={isBuy ? "text-emerald-400 border-emerald-500/40" : "text-red-400 border-red-500/40"}>
        {isBuy ? "BUY" : "SELL"}
      </Badge>
      <span className="text-muted-foreground truncate">{zone.entry_area_label}</span>
      <span className={`font-medium ${zone.conviction?.score_pct >= 55 ? "text-emerald-400" : zone.conviction?.score_pct >= 40 ? "text-amber-400" : "text-slate-400"}`}>
        {zone.conviction?.score_pct ?? "—"}%
      </span>
    </span>
  );
}

function WeeklyInstrumentCard({ cascade: c }) {
  if (c.error) return <ErrorCard label={c.label} symbol={c.symbol} />;

  const htf = c.htf_bias;
  const isRanging = c.cascade_stage === "ranging_no_trend";
  const progress = CASCADE_PROGRESS[c.cascade_stage] ?? 0;
  const armed = c.weekly_zone?.status === "ENTRY_ARMED";

  return (
    <Card
      className={`transition-all hover:border-amber-500/40 ${armed ? "border-emerald-400/60 shadow-emerald-400/20 shadow-lg" : ""}`}
      data-testid={`weekly-instrument-card-${c.symbol}`}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <CardTitle className="text-base" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                {c.label}
              </CardTitle>
              {htf?.combined && (
                <Badge variant="outline" className={`text-[9px] px-1.5 py-0 ${biasStyle[htf.combined] || biasStyle.NEUTRAL}`}
                  title={`Monthly ${htf.monthly} · Weekly ${htf.weekly} · Daily ${htf.daily}`} data-testid={`htf-bias-week-${c.symbol}`}>
                  HTF {htf.combined}
                </Badge>
              )}
            </div>
            <div className="text-2xl font-semibold mt-1">{fmt(c.current_price, c.symbol)}</div>
          </div>
          {armed && (
            <Badge className="bg-emerald-500/30 text-emerald-200 border-emerald-400/60 animate-pulse" data-testid={`weekly-zone-status-${c.symbol}`}>
              <Zap className="w-3 h-3 mr-1" />ENTRY ARMED
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isRanging ? (
          <div className="text-[11px] text-slate-400 flex items-center gap-1.5" data-testid={`weekly-ranging-badge-${c.symbol}`}>
            <Badge className="bg-slate-600/20 text-slate-400 border-slate-600/40">RANGING</Badge>
            No clear higher-timeframe trend - no Weekly trade surfaced for this asset right now.
          </div>
        ) : c.trade ? (
          /* Full senior-trader treatment: this asset's cascade produced a real
             Entry/Stop/Target - Confluence Breakdown + Senior Trader's Take + live
             chart, same as the Trade of the Week headline. */
          <TradeCard trade={c.trade} label={`${c.label} - Weekly Setup`} testid={`weekly-instrument-trade-${c.symbol}`} />
        ) : (
          <>
            {/* Weekly Forecast cascade progress */}
            <div className="space-y-1.5" data-testid={`weekly-cascade-${c.symbol}`}>
              <div className="text-[10px] uppercase tracking-wider text-purple-400 flex items-center gap-1">
                <Layers className="w-3 h-3" />
                Weekly Cascade
              </div>
              <div className="flex items-center gap-1.5">
                {CASCADE_STEPS.map((step, i) => {
                  const done = i < progress;
                  const active = i === progress;
                  return (
                    <div key={step} className="flex-1 flex flex-col items-center gap-1">
                      <div className={`h-1.5 w-full rounded-full ${done ? "bg-purple-500" : active ? "bg-purple-500/40" : "bg-slate-700"}`} />
                      <span className={`text-[9px] text-center leading-tight ${done ? "text-purple-300" : "text-muted-foreground"}`}>{step}</span>
                    </div>
                  );
                })}
              </div>
              <p className="text-[10px] text-muted-foreground" data-testid={`weekly-cascade-label-${c.symbol}`}>
                {CASCADE_STAGE_LABEL[c.cascade_stage] || c.cascade_stage} - nothing tradeable yet, here's the zone we have our eye on.
              </p>
            </div>

            {/* Nothing set up yet for this asset - show the zones being watched instead */}
            <ZoneReasonCard zone={c.buy_zone} symbol={c.symbol} kind="buy" />
            <div className="border-t border-slate-700/50" />
            <ZoneReasonCard zone={c.sell_zone} symbol={c.symbol} kind="sell" />
          </>
        )}
      </CardContent>
    </Card>
  );
}
